import {Component, DestroyRef, inject, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatSort} from "@angular/material/sort";
import {SelectionModel} from "@angular/cdk/collections";
import {Router, RouterLink} from "@angular/router";
import {
  faBadgeCheck,
  faBars,
  faBuilding,
  faDiagramProject,
  faEllipsisVertical,
  faFile,
  faFolderOpen,
  faGrid2,
  faGrip,
  faListCheck,
  faMagnifyingGlass,
  faPlus,
  faUser,
  faUserGroup
} from "@fortawesome/pro-solid-svg-icons";
import {Project, ProjectControllerService} from "../../api";
import {ToolbarService} from "../util/design/toolbar/toolbar.service";
import {StateConfigService} from "../../services/state/state-config.service";
import {PermissionService} from "../../services/permission/permission.service";
import {DeleteDialogComponent} from "../util/deleteDialog/delete-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator} from "@angular/material/paginator";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";
import {DecimalPipe} from "@angular/common";
import {BicCardComponent} from "../util/design/bic-card/bic-card.component";
import {
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicPopupComponent,
  BicToastService,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {serverErrorMessage} from "../my-checks/shared/server-error-message";
import {PageHeaderComponent} from "../util/design/page-header/page-header.component";
import {EmptyStateComponent} from "../util/design/empty-state/empty-state.component";

/**
 * Lists all Projects
 */
@Component({
  selector: 'app-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FaIconComponent,
    EmptyStateComponent,
    RouterLink,
    DecimalPipe,
    BicCardComponent,
    BicButtonComponent,
    BicDropdownComponent,
    BicDropdownElementComponent,
    BicDropdownTriggerDirective,
    BicPopupComponent,
    BicInputComponent,
    PageHeaderComponent,
    TooltipDirective
  ]
})
export class ProjectsComponent implements OnInit {
  permissionService = inject(PermissionService);
  private projectControllerService = inject(ProjectControllerService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private toolbar = inject(ToolbarService);
  private stateService = inject(StateConfigService);
  private toastService = inject(BicToastService);
  private destroyRef = inject(DestroyRef);
  private loadRequestId = 0;



  displayedColumns: string[] = ['select', 'name', 'image', 'id', 'projectManager', 'projectOwner', 'lastCheck', 'actions'];

  dataSource: MatTableDataSource<Project> = new MatTableDataSource<Project>();
  dataSourceWithObjectColumn = new MatTableDataSource<Project>;
  selection = new SelectionModel<Project>(true, []);

  isCardView: boolean = false;
  loading = true;

  leaveProjectBoolean: boolean = false;
  deleteProjectBoolean: boolean = false;
  deleteAndLeaveProjectBoolean: boolean = false;

  selectedProject!: Project;

  adminMap: Map<Project, boolean> = new Map<Project, boolean>();
  otherAdmins: Map<number, boolean> = new Map<number, boolean>();

  searchTerm: string = '';
  filteredProjects: Project[] = [];

  setCardView(): void {
    this.isCardView = true;
    localStorage.setItem('isProjectListView', 'false');
  }

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  @ViewChild('projectsTbSort') projectsTbSort = new MatSort();

  /**
   * All Project of User
   */
  projects: Project[] = [];
  projects_length: number = 0;
  //TODO: SEARCH FOR BETTER SOLUTION
  default_page_size: number = 1000;
  protected readonly faSpinnerThird = faSpinnerThird;

  /**
   * Initialization of Component
   */

  ngOnInit(): void {

    this.stateService.organisationChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadProjects();
    });
    this.loadProjects();
    this.setCardView();
  }

  deleteProjects() {
    let deleteDialog = this.dialog.open(DeleteDialogComponent, {
      panelClass:"rounded-corners-dialog",
      data: {
        projects: this.selection.selected
      }
    }).afterClosed().subscribe(() => {
      const organisationId = this.stateService.getOrganisationIdOrUndefined();
      if (organisationId) {
        this.projectControllerService.getProjectsByOrganisationId(organisationId).subscribe(projects => {
          this.dataSource = new MatTableDataSource(projects);
          this.selection.clear();
        });
      } else if (this.stateService.isPersonal) {
        this.projectControllerService.getPersonalProjects().subscribe(projects => {
          this.dataSource = new MatTableDataSource(projects);
          this.selection.clear();
        });
      } else {
        this.dataSource = new MatTableDataSource<Project>([]);
        this.selection.clear();
      }
    });
  }

  leaveProject(project: Project) {
    this.projectControllerService.leaveProject(project.id!).subscribe(() => {
      this.loadProjects();
    })

  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.data);
  }

  checkboxLabel(row?: Project): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id!}`;
  }

  navigateToProject(row: Project) {
    this.stateService.selectProject(row.id!);

  }

  navigateToProjectSettings(project: Project) {
    this.router.navigate(['/projects', project.id, 'settings']);
  }

  calculateSizeInMB(size: number): number {
    return size / 1024 / 1024;
  }

  calculateSizeInGB(size: number): number {
    return size / 1024 / 1024 / 1024;
  }

  selectProject(row: Project) {
    this.selection.toggle(row);
  }

  openLeaveProject(project: Project){
    this.leaveProjectBoolean = true;
    this.selectedProject = project;
  }

  openDeleteProject(project: Project){
    this.deleteProjectBoolean = true;
    this.selectedProject = project;
  }

  openDeleteAndLeaveProject(project: Project){
    this.deleteAndLeaveProjectBoolean = true;
    this.selectedProject = project;
  }

  deleteProject(project: Project){
    this.projectControllerService.deleteProject(project.id!).subscribe({
      next: () => this.loadProjects(),
      error: err => this.toastService.error(serverErrorMessage(err,
        `Das Projekt „${project.name}“ konnte nicht gelöscht werden.`)),
    });
    this.deleteProjectBoolean = false;
    this.deleteAndLeaveProjectBoolean = false;
  }

  getIsAdmin(project: Project){
    return this.adminMap.get(project);
  }

  canLeaveProject(project: Project): boolean {
    if (this.getIsAdmin(project)) {
      if (!project.id) {
        return false;
      }
      return this.otherAdmins.get(project.id) === true;
    }

    return project.users?.length !== 1;
  }

  getAdminProjectIds(): number[] {
    const adminProjectIds: number[] = [];
    this.adminMap.forEach((isAdmin, project) => {
      if (isAdmin) {
        if (project.id) {
          adminProjectIds.push(project.id);
        }
      }
    });
    return adminProjectIds;
  }

  convertToMap(obj: { [key: string]: boolean }): Map<number, boolean> {
    const map = new Map<number, boolean>();
    for (const [key, value] of Object.entries(obj)) {
      const numericKey = parseInt(key, 10);
      if (!isNaN(numericKey)) {
        map.set(numericKey, value);
      }
    }
    return map;
  }

  private applySearchFilter(projects: Project[]): Project[] {
    if (!projects.length || !this.searchTerm.trim()) {
      return projects;
    }
    const query = this.searchTerm.trim().toLowerCase();
    return projects.filter(project => (project.name ?? '').toLowerCase().includes(query));
  }

  onSearchTermChange(value: string): void {
    this.searchTerm = value;
    this.filteredProjects = this.applySearchFilter(this.projects ?? []);
  }

  protected readonly faBars = faBars;
  protected readonly faMagnifyingGlass = faMagnifyingGlass;
  protected readonly faGrip = faGrip;
  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faPlus = faPlus;
  protected readonly faDiagramProject = faDiagramProject;
  protected readonly faFile = faFile;
  protected readonly faUser = faUser;
  protected readonly faListCheck = faListCheck;
  protected readonly faUserGroup = faUserGroup;
  protected readonly faBuilding = faBuilding;
  protected readonly faFolderOpen = faFolderOpen;
  protected readonly faGrid2 = faGrid2;

  loadProjects() {
    this.loading = true;
    const requestId = ++this.loadRequestId;
    this.projects = [];
    this.dataSource = new MatTableDataSource<Project>([]);
    this.filteredProjects = [];
    this.selection.clear();
    this.adminMap.clear();
    this.otherAdmins = new Map<number, boolean>();
    const organisationId = this.stateService.getOrganisationIdOrUndefined();
    if (organisationId) {
      this.projectControllerService.getProjectsByOrganisationId(organisationId).subscribe(projects => {
        if (requestId !== this.loadRequestId) return;
        this.projects = projects;
        let datasource_temp = projects.slice(0,this.default_page_size);
        // datasource_temp.forEach(project => {
        //   if(this.permissionService.hasProjectPermission(project.id!, "ROLE_PROJECT_CHECKER")){
            // this.projectControllerService.getLatestEvaluationForProject(project.id!).subscribe(evaluation => {
            //   this.projectEvaluationMap.set(project, evaluation);
            // });
          // }
        // });
        this.dataSource = new MatTableDataSource(datasource_temp);
        this.filteredProjects = this.applySearchFilter(this.projects);
        this.projects.forEach(project => {
          this.stateService.projects.get(project.id ?? 0)?.privileges?.length == 11 ? this.adminMap.set(project, true) : this.adminMap.set(project, false);
        })
        this.projectControllerService.otherAdmins(this.getAdminProjectIds()).subscribe(admins => {
          this.otherAdmins = this.convertToMap(admins);
        });
        this.loading = false;

      });
    } else if (this.stateService.isPersonal) {
      this.projectControllerService.getPersonalProjects().subscribe(projects => {
        if (requestId !== this.loadRequestId) return;
        this.projects = projects;
        let datasource_temp = projects.slice(0,this.default_page_size);

        this.dataSource = new MatTableDataSource(datasource_temp);
        this.filteredProjects = this.applySearchFilter(this.projects);
        this.projects.forEach(project => {
          this.stateService.projects.get(project.id ?? 0)?.privileges?.length == 11 ? this.adminMap.set(project, true) : this.adminMap.set(project, false);
        })

        this.projectControllerService.otherAdmins(this.getAdminProjectIds()).subscribe(admins => {
          this.otherAdmins = this.convertToMap(admins);
        });
        this.loading = false;

      });
    } else {
      this.projects = [];
      this.dataSource = new MatTableDataSource<Project>([]);
      this.filteredProjects = [];
      this.otherAdmins = new Map<number, boolean>();
      this.adminMap.clear();
      this.loading = false;
    }

  }

  getUserTitle(): string {
    const user = this.stateService.user;
    if (!user) return '';

    let title = '';

    // Add salutation if it exists and is not 'NONE'
    if (user.salutation && user.salutation !== 'NONE') {
      title += user.salutation === 'HERR' ? 'Herr ' : 'Frau ';
    }

    // Add title if it exists and is not 'NONE'
    if (user.title && user.title !== 'NONE') {
      const titleMap: { [key: string]: string } = {
        'DR': 'Dr. ',
        'PROF': 'Prof. ',
        'PROFDR': 'Prof. Dr. '
      };
      title += titleMap[user.title] || '';
    }

    return title;
  }

  protected readonly faBadgeCheck = faBadgeCheck;

  getUserLastName(): string {
    return this.stateService.user?.lastName ?? '';
  }
  protected readonly event = event;

  /**
   * Ohne Organisation bzw. Persoenlich-Kontext gibt es keine Projektliste. In der Navigation ist
   * der Weg hierher gesperrt, ueber die URL ist die Seite aber weiterhin erreichbar - dann soll
   * statt "Erstellen Sie Ihr erstes Projekt" der Hinweis auf die Organisationsauswahl kommen.
   */
  get hasProjectContext(): boolean {
    return !!this.stateService.getOrganisationIdOrUndefined() || this.stateService.isPersonal;
  }

  get canCreateProject(): boolean {
    const orgId = this.stateService.getOrganisationIdOrUndefined();
    if (!orgId) {
      return this.stateService.isPersonal;
    }

    // Prüft POSITIV, ob der Nutzer Edit-/Erstellungs-Rechte in der Organisation hat
    return this.permissionService.hasOrganisationPrivilege('ORGANISATION_PROJECT_EDIT', orgId);
  }
}
